import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, Tabs, Tab } from '@mui/material'
import { Add } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { listReceipts, listPayments, type ReceiptListItem, type PaymentListItem, type PaymentStatus } from '../../api/financiar'
import ReceiptDialog from './ReceiptDialog'
import PaymentDialog from './PaymentDialog'

const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = { Draft: 'Ciornă', Executata: 'Executată', Anulata: 'Anulată' }
const PAYMENT_STATUS_COLOR: Record<PaymentStatus, 'default' | 'success' | 'error'> = { Draft: 'default', Executata: 'success', Anulata: 'error' }

export default function IncasariPlatiPage() {
  const [tab, setTab] = useState(0)
  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Financiar</Typography>
        <Typography variant="inherit" color="text.primary">Încasări și plăți</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Încasări și plăți</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Încasări" />
        <Tab label="Plăți" />
      </Tabs>
      {tab === 0 ? <IncasariTab /> : <PlatiTab />}
    </Stack>
  )
}

function IncasariTab() {
  const [target, setTarget] = useState<ReceiptListItem | null | 'new'>(null)
  const { data, isLoading } = useQuery({ queryKey: ['financiar', 'incasari'], queryFn: listReceipts })

  const columns = useMemo<GridColDef<ReceiptListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 150, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'date', headerName: 'Dată', width: 110 },
    { field: 'clientName', headerName: 'Client', flex: 1, minWidth: 180 },
    { field: 'cashAccountName', headerName: 'Cont', width: 150 },
    { field: 'amount', headerName: 'Sumă', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    { field: 'allocatedAmount', headerName: 'Alocat', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    {
      field: 'status', headerName: 'Status', width: 110, sortable: false,
      renderCell: (p) => p.row.cancelledAt
        ? <Chip size="small" label="Anulată" color="error" variant="outlined" />
        : p.row.allocatedAmount < p.row.amount ? <Chip size="small" label="Cu avans" color="info" variant="outlined" /> : null,
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setTarget('new')}>Încasare nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={(p) => setTarget(p.row)}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <ReceiptDialog target={target} onClose={() => setTarget(null)} />
    </Stack>
  )
}

function PlatiTab() {
  const [target, setTarget] = useState<PaymentListItem | null | 'new'>(null)
  const { data, isLoading } = useQuery({ queryKey: ['financiar', 'plati'], queryFn: listPayments })

  const columns = useMemo<GridColDef<PaymentListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 150, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'date', headerName: 'Dată', width: 110 },
    { field: 'supplierName', headerName: 'Furnizor', flex: 1, minWidth: 180, renderCell: (p) => p.row.supplierName ?? '—' },
    { field: 'cashAccountName', headerName: 'Cont', width: 150 },
    { field: 'amount', headerName: 'Sumă', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    {
      field: 'status', headerName: 'Status', width: 130, sortable: false,
      renderCell: (p) => <Chip size="small" label={PAYMENT_STATUS_LABEL[p.row.status]} color={PAYMENT_STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    {
      field: 'isRecurring', headerName: '', width: 130, sortable: false,
      renderCell: (p) => p.row.isRecurring ? <Chip size="small" label="recurentă" /> : null,
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setTarget('new')}>Plată nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={(p) => setTarget(p.row)}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <PaymentDialog target={target} onClose={() => setTarget(null)} />
    </Stack>
  )
}
